// «Mi entrega»: la lección pide subir archivos (como los «Assignments» de LearnDash).
// Muestra las instrucciones, el estado de lo enviado y el comentario del tutor, y deja
// volver a enviar si la devolvió. Va dentro del modo Mentor y usa sus variables de tema.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { entregasService, descargarEntrega } from '../../services/api';
import { fmtFechaHoraBogota } from '../../utils/fecha';

export const ESTADOS_ENTREGA = {
  pendiente: { txt: 'En revisión', bg: 'rgba(245,158,11,.15)', color: '#d97706' },
  aprobada: { txt: 'Aprobada', bg: 'rgba(16,185,129,.15)', color: '#059669' },
  automatica: { txt: 'Recibida', bg: 'rgba(16,185,129,.15)', color: '#059669' },
  devuelta: { txt: 'Devuelta para corregir', bg: 'rgba(239,68,68,.14)', color: '#dc2626' },
};

// La fecha límite se guarda en hora Colombia ('2026-11-15T23:59').
export const fmtLimite = (f) => (f ? new Date(`${f}:00-05:00`).toLocaleString('es-CO', {
  timeZone: 'America/Bogota', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit',
}) : '');

// Desglose de la rúbrica que calificó el tutor.
export const Rubrica = ({ filas }) => (
  <table className="rubrica">
    <tbody>
      {filas.map((r, i) => <tr key={i}><td>{r.criterio}</td><td>{r.puntos} / {r.max}</td></tr>)}
    </tbody>
  </table>
);

const tamano = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const MiEntrega = ({ leccionId, entrega: config, onEstado }) => {
  const [datos, setDatos] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [error, setError] = useState('');
  const [arrastrando, setArrastrando] = useState(false);
  const [cambiando, setCambiando] = useState(false);   // reemplazar el archivo de una entrega que espera revisión
  const input = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await entregasService.mia(leccionId);
      setDatos(r.data);
      onEstado?.(r.data.entregas?.[0]?.estado || null);
    } catch (e) { setDatos({ error: true }); }
  }, [leccionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setDatos(null); setArchivos([]); setComentario(''); setError(''); setCambiando(false); cargar(); }, [cargar]);

  const cfg = datos?.config || config;
  const ext = cfg?.extensiones || [];
  const agregar = (lista) => {
    setError('');
    const nuevos = [...archivos];
    for (const f of Array.from(lista || [])) {
      const e = (f.name.split('.').pop() || '').toLowerCase();
      if (ext.length && !ext.includes(e)) { setError(`«${f.name}» no es de un tipo permitido (${ext.join(', ')})`); continue; }
      if (f.size > (cfg.max_mb || 20) * 1048576) { setError(`«${f.name}» supera ${cfg.max_mb} MB`); continue; }
      if (nuevos.length >= (cfg.max_archivos || 1)) { setError(`Puedes subir máximo ${cfg.max_archivos} archivo(s)`); break; }
      nuevos.push(f);
    }
    setArchivos(nuevos);
  };

  const enviar = async () => {
    if (!archivos.length) { setError('Adjunta al menos un archivo'); return; }
    setEnviando(true); setError(''); setProgreso(0);
    try {
      const r = await entregasService.enviar(leccionId, archivos, comentario, ev => ev.total && setProgreso(Math.round((ev.loaded / ev.total) * 100)), cambiando);
      setArchivos([]); setComentario(''); setCambiando(false);
      await cargar();
      if (r.data?.completada) onEstado?.('automatica', true);
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo enviar. Revisa tu conexión e inténtalo de nuevo.');
    }
    setEnviando(false);
  };

  if (!cfg) return null;
  const historial = datos?.entregas || [];
  const ultima = historial[0];
  const est = ultima ? ESTADOS_ENTREGA[ultima.estado] : null;

  return (
    <Caja>
      <Cabeza>
        <strong>📎 Mi entrega</strong>
        {est && <Estado style={{ background: est.bg, color: est.color }}>{est.txt}</Estado>}
      </Cabeza>
      {cfg.instrucciones && <Instr>{cfg.instrucciones}</Instr>}
      {cfg.fecha_limite && (!ultima || ultima.estado === 'devuelta') && (
        <p className={datos?.vencida ? 'limite vencida' : 'limite'}>
          {datos?.vencida
            ? (cfg.permitir_tarde ? `La fecha límite fue el ${fmtLimite(cfg.fecha_limite)}. Aún puedes enviarla, pero quedará marcada como tarde.` : `La fecha límite fue el ${fmtLimite(cfg.fecha_limite)}. Ya no se reciben entregas.`)
            : `Entrega hasta el ${fmtLimite(cfg.fecha_limite)}.`}
        </p>
      )}

      {ultima && (
        <Envio>
          <div className="meta">
            Enviada el {fmtFechaHoraBogota(ultima.enviado_at)}{ultima.intento > 1 ? ` · intento ${ultima.intento}` : ''}{ultima.tarde ? ' · fuera de plazo' : ''}
          </div>
          <Archivos>
            {ultima.archivos.map(a => (
              <button key={a.i} type="button" onClick={() => descargarEntrega(ultima.id, a.i, a.nombre)}>📄 {a.nombre} <span>{tamano(a.tamano)}</span></button>
            ))}
          </Archivos>
          {ultima.estado === 'pendiente' && (
            <p className="nota">
              Tu tutor la revisará. La lección se completa cuando la apruebe.
              {datos?.puede_reemplazar && !cambiando && (
                <> <button type="button" className="enlace" onClick={() => { setCambiando(true); setComentario(ultima.comentario_alumno || ''); }}>¿Te equivocaste de archivo? Cámbialo</button></>
              )}
            </p>
          )}
          {(ultima.comentario_tutor || ultima.nota !== null || ultima.archivos_tutor?.length > 0) && (
            <Tutor $ok={ultima.estado === 'aprobada'}>
              <b>Comentario del tutor{ultima.nota !== null ? ` · Nota: ${ultima.nota}` : ''}</b>
              {ultima.rubrica?.length > 0 && <Rubrica filas={ultima.rubrica} />}
              {ultima.comentario_tutor && <p>{ultima.comentario_tutor}</p>}
              {ultima.archivos_tutor?.length > 0 && (
                <Archivos>
                  {ultima.archivos_tutor.map(a => (
                    <button key={a.i} type="button" onClick={() => descargarEntrega(ultima.id, a.i, a.nombre, true)}>📄 {a.nombre}</button>
                  ))}
                </Archivos>
              )}
            </Tutor>
          )}
        </Envio>
      )}

      {(datos?.puede_enviar || (cambiando && datos?.puede_reemplazar)) && (
        <Form>
          {ultima?.estado === 'devuelta' && <p className="nota">Corrige lo que te indicó el tutor y vuelve a enviar.</p>}
          {cambiando && <p className="nota">El archivo nuevo reemplaza al que enviaste. Tu tutor verá solo el nuevo.</p>}
          <Zona
            $activa={arrastrando}
            onClick={() => input.current?.click()}
            onDragOver={e => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={e => { e.preventDefault(); setArrastrando(false); agregar(e.dataTransfer.files); }}
          >
            <span>Arrastra aquí tu archivo o <u>búscalo en tu equipo</u></span>
            <small>{ext.join(', ').toUpperCase()} · máx. {cfg.max_mb} MB{cfg.max_archivos > 1 ? ` · hasta ${cfg.max_archivos} archivos` : ''}</small>
            <input ref={input} type="file" hidden multiple={cfg.max_archivos > 1} accept={ext.map(e => '.' + e).join(',')}
              onChange={e => { agregar(e.target.files); e.target.value = ''; }} />
          </Zona>
          {archivos.length > 0 && (
            <Archivos>
              {archivos.map((f, i) => (
                <span key={i} className="sel">📄 {f.name} <span>{tamano(f.size)}</span>
                  <button type="button" aria-label={`Quitar ${f.name}`} onClick={() => setArchivos(archivos.filter((_, j) => j !== i))}>✕</button>
                </span>
              ))}
            </Archivos>
          )}
          <textarea rows={2} placeholder="Comentario para el tutor (opcional)" value={comentario} onChange={e => setComentario(e.target.value)} />
          {error && <p className="error">{error}</p>}
          <Boton type="button" onClick={enviar} disabled={enviando || !archivos.length}>
            {enviando ? `Enviando… ${progreso}%` : cambiando ? 'Reemplazar archivo' : 'Enviar entrega'}
          </Boton>
          {cambiando && <button type="button" className="enlace" onClick={() => { setCambiando(false); setArchivos([]); setError(''); }}>Cancelar</button>}
        </Form>
      )}
      {datos?.error && <p className="error">No se pudo cargar tu entrega.</p>}
    </Caja>
  );
};

export default MiEntrega;

const Caja = styled.section`
  margin-top: 0.6rem; padding: 0.9rem 1rem; border-radius: 10px;
  border: 1px solid var(--m-border-light, rgba(148,163,184,.3)); background: var(--m-bg-card, rgba(148,163,184,.06));
  color: var(--m-text-secondary, #475569); font-size: 0.9rem; line-height: 1.5;
  .nota { margin: 0.4rem 0 0; font-size: 0.84rem; color: var(--m-text-tertiary, #64748b); }
  .error { margin: 0.4rem 0 0; color: #ef4444; font-size: 0.85rem; }
  .limite { margin: 0.4rem 0 0; font-size: 0.84rem; font-weight: 600; color: var(--m-text-secondary, #475569); }
  .limite.vencida { color: #dc2626; }
  .rubrica { margin: 0.35rem 0 0; border-collapse: collapse; font-size: 0.84rem; }
  .rubrica td { padding: 0.15rem 1rem 0.15rem 0; }
  .rubrica td:last-child { font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
  .enlace { border: 0; background: none; padding: 0; color: #14b6cb; text-decoration: underline; cursor: pointer; font: inherit; font-size: 0.84rem; align-self: flex-start; }
  @media (max-width: 768px) { border-radius: 0; }
`;
const Cabeza = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; flex-wrap: wrap;
  strong { color: var(--m-text-primary, #1e293b); font-size: 0.95rem; }
`;
const Estado = styled.span`padding: 0.15rem 0.65rem; border-radius: 999px; font-size: 0.75rem; font-weight: 700;`;
const Instr = styled.p`margin: 0.4rem 0 0; white-space: pre-line;`;
const Envio = styled.div`
  margin-top: 0.7rem; padding-top: 0.7rem; border-top: 1px solid var(--m-border, rgba(148,163,184,.2));
  .meta { font-size: 0.8rem; color: var(--m-text-tertiary, #64748b); }
`;
const Archivos = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.4rem;
  > button, .sel {
    display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.35rem 0.7rem; border-radius: 8px;
    border: 1px solid var(--m-border-light, rgba(148,163,184,.3)); background: var(--m-bg-input, transparent);
    color: var(--m-text-primary, #1e293b); font: inherit; font-size: 0.82rem; cursor: pointer; max-width: 100%;
    overflow-wrap: anywhere; text-align: left;
  }
  > button:hover { border-color: #14b6cb; }
  span span, button span { color: var(--m-text-muted, #94a3b8); font-size: 0.75rem; }
  .sel button { border: 0; background: none; color: var(--m-text-muted, #94a3b8); cursor: pointer; padding: 0 0.1rem; font-size: 0.85rem; }
`;
const Tutor = styled.div`
  margin-top: 0.6rem; padding: 0.6rem 0.8rem; border-radius: 8px;
  border-left: 3px solid ${p => (p.$ok ? '#10b981' : '#f59e0b')}; background: var(--m-bg-hover, rgba(148,163,184,.08));
  b { display: block; color: var(--m-text-primary, #1e293b); font-size: 0.82rem; }
  p { margin: 0.2rem 0 0; white-space: pre-line; }
`;
const Form = styled.div`
  margin-top: 0.7rem; display: flex; flex-direction: column; gap: 0.5rem;
  textarea {
    width: 100%; box-sizing: border-box; padding: 0.5rem 0.7rem; border-radius: 8px; resize: vertical; font: inherit; font-size: 0.86rem;
    border: 1px solid var(--m-border-light, rgba(148,163,184,.3)); background: var(--m-bg-input, #fff); color: var(--m-text-primary, #1e293b);
  }
`;
const Zona = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 0.2rem; padding: 1rem; border-radius: 10px; cursor: pointer; text-align: center;
  border: 1.5px dashed ${p => (p.$activa ? '#14b6cb' : 'var(--m-border-light, rgba(148,163,184,.4))')};
  background: ${p => (p.$activa ? 'rgba(20,182,203,.08)' : 'transparent')};
  color: var(--m-text-secondary, #475569);
  u { color: #14b6cb; }
  small { color: var(--m-text-muted, #94a3b8); font-size: 0.75rem; }
`;
const Boton = styled.button`
  align-self: flex-start; padding: 0.55rem 1.2rem; border: 0; border-radius: 8px; background: #14b6cb; color: #fff;
  font: inherit; font-weight: 700; font-size: 0.88rem; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: default; }
`;
